import * as React from "react";
import { Copy } from "lucide-react";
import { ACTION_BUTTON_STYLE } from "../constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { extractCitation, removeCitations } from "@/modules/chat/chat.utils";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { copyAsRichText, copyTextToClipboard } from "@/helpers/clipboard";
import { useCopyAction } from "@/hooks/useCopyAction";
import { CopyActionButton } from "./CopyActionButton";
import type { ChatMessage, Evidence } from "../types";

interface Props {
  content: string;
  message?: ChatMessage;
  evidences?: Evidence[];
  contentRef?: React.RefObject<HTMLDivElement | null>;
}

export const CopyButton = ({ content, message, evidences = [], contentRef }: Props) => {
  const { copied, executeCopy } = useCopyAction();
  const { rootItem } = useCurrentPdf();

  const handleCopy = async (withCitations: boolean) => {
    await executeCopy(async () => {
      let textToCopy: string;
      if (withCitations && message && rootItem && evidences.length > 0) {
        const messageForExtraction = { content: message.content, isUser: message.type === "human" };
        textToCopy = extractCitation(messageForExtraction, rootItem, evidences);
      } else if (withCitations) {
        textToCopy = content.trim();
      } else {
        textToCopy = removeCitations(content);
      }

      if (contentRef?.current) {
        await copyAsRichText(contentRef.current, textToCopy);
      } else {
        await copyTextToClipboard(textToCopy);
      }
    });
  };

  if (copied) {
    return <CopyActionButton copied onCopy={() => {}} />;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={ACTION_BUTTON_STYLE}>
          <Copy size={14} className="mr-1" />
          Copy
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={() => handleCopy(false)}>Copy without citations</DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleCopy(true)}>Copy with citations</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
